import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAuth, usePermission } from '@saas-kit/lib';
import {
  ErrorBanner,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { InboxTab } from './InboxTab';
import { PreferencesTab } from './PreferencesTab';

type TabKey = 'inbox' | 'preferences';

export function NotificationsPage(): JSX.Element {
  const { t } = useTranslation('notifications');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canRead = usePermission('notifications.read');
  const canPreferences = usePermission('notifications.preferences');
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey =
    searchParams.get('tab') === 'preferences' ? 'preferences' : 'inbox';

  function changeTab(next: string): void {
    setSearchParams(next === 'preferences' ? { tab: 'preferences' } : {}, {
      replace: true,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      {!selectedTenant ? (
        <TenantRequiredState
          title={t('tenant_required.title')}
          body={t('tenant_required.body')}
          cta={t('tenant_required.cta')}
        />
      ) : (
        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList>
            <TabsTrigger value="inbox">{t('tabs.inbox')}</TabsTrigger>
            <TabsTrigger value="preferences">
              {t('tabs.preferences')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inbox">
            {canRead ? (
              <InboxTab />
            ) : (
              <ErrorBanner>{t('errors.denied')}</ErrorBanner>
            )}
          </TabsContent>

          <TabsContent value="preferences">
            {canPreferences ? (
              <PreferencesTab />
            ) : (
              <ErrorBanner>{t('errors.preferences_denied')}</ErrorBanner>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
